<template>
  <v-app class="login-img">
    <v-snackbar v-model="snackbar" timeout="3000" :color="snackbarColor">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false"> Close </v-btn>
    </v-snackbar>
    <v-navigation-drawer
      color="sidebar"
      :class="['login-sidebar pa-7 elevation-6 rounded-right', { 'white-menu-sidebar': useWhiteMenu }]"
      permanent
      floating
    >
      <router-link to="/">
        <v-img :src="logo" max-width="175" class="mx-auto"></v-img>
      </router-link>
      <v-alert type="error" class="mt-5" v-if="errorMessage === true && loginInvalid">
        This account is {{ loginInvalid }}, please contact support.
      </v-alert>
      <v-alert type="error" class="mt-5" v-if="error === true">
        {{ errorMessage }}
      </v-alert>

      <v-tabs centered grow background-color="transparent" dark>
        <v-tab> Sign In </v-tab>
        <v-tab> Register </v-tab>
        <v-tab-item>
          <!-- Sign in form -->
          <v-form id="form" ref="form" class="mt-5">
            <!-- Fields for the provider -->
            <template v-if="userType === 'provider'">
              <v-card-text class="pb-0">
                <v-text-field
                  v-model="user.email"
                  outlined
                  dark
                  color="white"
                  prepend-inner-icon="mdi-email"
                  label="Email"
                  type="email"
                  :rules="emailRules"
                  @keydown.enter="submit"
                ></v-text-field>
                <v-text-field
                  v-model="user.password"
                  outlined
                  dark
                  color="white"
                  prepend-inner-icon="mdi-lock"
                  label="Password"
                  type="password"
                  :rules="passwordRule"
                  @keydown.enter="submit"
                ></v-text-field>
              </v-card-text>
              <v-card-actions class="mx-auto" style="flex-direction: column">
                <v-spacer />
                <v-btn
                  :loading="loading"
                  depressed
                  color="secondary"
                  class="px-5 mb-3"
                  @click="submit"
                >
                  Sign in
                </v-btn>
                <v-btn
                  color="secondary"
                  class="px-5 mb-3"
                  text
                  @click="forgotPasswordDialog = true"
                >
                  Forgot password?
                </v-btn>
                <v-btn
                  v-if="showSSOButton"
                  color="secondary"
                  class="px-5 mb-3"
                  text
                  @click="ssoLogin"
                >
                  SSO login
                </v-btn>
                <v-spacer />
              </v-card-actions>
            </template>

            <!-- Fields for the member -->
            <template v-else>
              <v-card-text class="pb-0">
                <v-form class="mt-5">
                  <v-text-field
                    v-model="user.email"
                    label="Email"
                    dark
                    color="white"
                    outlined
                    prepend-icon="mdi-account"
                  ></v-text-field>
                  <v-text-field
                    v-model="user.password"
                    dark
                    color="white"
                    prepend-icon="mdi-lock"
                    label="Password"
                    type="password"
                    outlined
                    @keydown.enter="submit"
                  ></v-text-field>
                </v-form>
              </v-card-text>
              <v-card-actions class="mx-auto" style="flex-direction: column">
                <v-spacer />
                <v-btn
                  :loading="loading"
                  depressed
                  color="secondary"
                  class="px-5 mb-3"
                  @click="submit"
                >
                  Sign in
                </v-btn>
                <v-btn
                  color="secondary"
                  class="px-5 mb-3"
                  text
                  @click="forgotPasswordDialog = true"
                >
                  Forgot password?
                </v-btn>
                <v-spacer />
              </v-card-actions>
            </template>
          </v-form>
        </v-tab-item>
        <v-tab-item border-white>
          <!-- Registration form -->
          <v-form ref="form" class="mt-5">
            <!-- Fields for the provider -->
            <template v-if="userType === 'provider'">
              <v-card-text class="pb-0">
                <v-text-field
                  v-model="user.name"
                  outlined
                  dark
                  color="white"
                  prepend-inner-icon="mdi-account"
                  :label="isFHCClient ? 'Practice Name' : 'Name'"
                  :rules="[(v) => !!v || (isFHCClient ? 'Practice Name is required' : 'Name is required')]"
                  type="text"
                  @keydown.enter="register"
                ></v-text-field>
                <v-text-field
                  v-model="user.npi"
                  outlined
                  dark
                  color="white"
                  prepend-inner-icon="mdi-medical-bag"
                  :label="isFHCClient ? 'Practice NPI' : 'NPI'"
                  :rules="[
                    (v) => !!v || (isFHCClient ? 'Practice NPI is required' : 'NPI is required'),
                    // (v) => (v && v.toString().length === 10) || 'NPI must be a 10-digit number',
                  ]"
                  type="text"
                  @keydown.enter="register"
                ></v-text-field>
                <v-text-field
                  v-model="user.email"
                  outlined
                  dark
                  color="white"
                  prepend-inner-icon="mdi-email"
                  label="Email"
                  type="email"
                  :rules="emailRules"
                  @keydown.enter="register"
                ></v-text-field>
                <v-text-field
                  v-model="user.password"
                  outlined
                  :rules="passwordRules"
                  :type="false ? 'text' : 'password'"
                  dark
                  color="white"
                  prepend-inner-icon="mdi-lock"
                  label="Password"
                  @keydown.enter="register"
                ></v-text-field>
                <v-text-field
                  v-model="user.confirmPassword"
                  outlined
                  dark
                  :rules="passwordConfirm"
                  :type="false ? 'text' : 'password'"
                  color="white"
                  prepend-inner-icon="mdi-lock"
                  label="Confirm Password"
                  @keydown.enter="register"
                ></v-text-field>
                <v-text-field
                  v-model="user.authCode"
                  outlined
                  dark
                  :rules="[
                    (v) => !!v || 'Authorization code is required',
                    (v) => (v && (v.toLowerCase() === 'clearspring' || v.toLowerCase() === 'elderone' || v.toLowerCase() === 'fhc'))
                             || 'Authorization code is incorrect',
                  ]"
                  color="white"
                  prepend-inner-icon="mdi-key"
                  label="Authentication Code"
                  type="text"
                  @keydown.enter="register"
                ></v-text-field>
                <v-card-actions class="mx-auto">
                  <v-spacer />
                  <v-btn
                    :loading="loading"
                    depressed
                    color="secondary"
                    class="px-5 mb-3"
                    @click="register"
                  >
                    Register
                  </v-btn>
                  <v-spacer />
                </v-card-actions>
              </v-card-text>
            </template>
            <!-- Fields for the member -->
            <template v-else>
              <!-- Member specific registration fields go here -->
              <v-card-text class="pb-0">
                <v-alert
                  type="error"
                  v-if="errorMessage === true"
                  transition="scale-transition"
                  rounded
                  contain
                >
                  No records found for this user.
                </v-alert>
                <v-text-field
                  v-model="member.subscriberId"
                  prepend-icon="mdi-numeric"
                  label="Subscriber Number"
                  type="text"
                  dark
                  color="white"
                  outlined
                  @keydown.enter="submit"
                ></v-text-field>
                <v-text-field
                  v-model="member.firstName"
                  prepend-icon="mdi-account-box"
                  dark
                  color="white"
                  label="First Name"
                  type="text"
                  outlined
                  @keydown.enter="submit"
                ></v-text-field>
                <v-text-field
                  v-model="member.lastName"
                  prepend-icon="mdi-account-box-multiple"
                  label="Last Name"
                  dark
                  color="white"
                  type="text"
                  outlined
                  @keydown.enter="submit"
                ></v-text-field>
                <v-menu
                  ref="menu"
                  v-model="menu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="member.birthDate"
                      label="Date of Birth"
                      prepend-icon="mdi-cake"
                      color="white"
                      dark
                      outlined
                      v-bind="attrs"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="member.birthDate"
                    show-current="false"
                    no-title
                    scrollable
                  >
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="menu = false"> Cancel </v-btn>
                    <v-btn text color="primary" @click="$refs.menu.save(member.birthDate)">
                      OK
                    </v-btn></v-date-picker
                  >
                </v-menu>
              </v-card-text>
              <v-card-actions class="mx-auto">
                <v-spacer />
                <v-btn
                  :loading="loading"
                  depressed
                  color="secondary"
                  class="px-5 mb-3"
                  @click="findRecords"
                >
                  Register
                </v-btn>
                <v-spacer />
              </v-card-actions>
            </template>
          </v-form>
        </v-tab-item>
      </v-tabs>

      <register-user @login="login" :dialog="registerDialog" :user="member" @loadingButton="setLoadingButton" />
      <!-- Forgot password dialog -->
      <v-dialog v-model="forgotPasswordDialog" max-width="500px">
        <v-card>
          <v-card-title class="headline">Forgot Password</v-card-title>
          <v-card-text>Please enter your email address.</v-card-text>
          <v-card-text>
            <v-text-field
              label="Email"
              v-model="email"
              outlined
              :rules="emailRules"
              required
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="forgotPasswordDialog = false">Cancel</v-btn>
            <v-btn color="primary" :loading="loading" text @click="sendResetEmail"
              >Send Reset Email</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Add the dialog box in your template -->
      <v-dialog transition="dialog-bottom-transition" v-model="dialog" persistent max-width="500px">
        <v-card>
          <v-card-title>
            <span class="headline">Are you a member or a provider?</span>
          </v-card-title>
          <v-card-text>
            <v-radio-group v-model="userType" row>
              <v-radio label="Provider" value="provider"></v-radio>
              <v-radio label="Member" value="member"></v-radio>
            </v-radio-group>
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn color="primary" text @click="setUserType">Ok</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <template v-slot:append>
        <v-btn color="error" @click="dialog = true" width="100%" class="mb-3">
          <span>{{ changeUserText }}</span>
        </v-btn>
      </template>
    </v-navigation-drawer>

    <div class="welcome-text d-none d-md-block black--text text--lighten-1">
      <div class="display-3">{{ portalName }}</div>
      <div class="overline">{{ portalTagline }}</div>
    </div>
  </v-app>
</template>

<script>
import axios from 'axios';
import RegisterUser from '../components/RegisterUser.vue';
import Mixin from '../utils/Mixin';

export default {
  mixins: [Mixin],
  components: {
    RegisterUser,
  },
  data() {
    return {
      user: {
        email: '',
        name: '',
        password: '',
        confirmPassword: '',
        insurance: '',
        npi: '',
        authCode: '',
        recaptcha: '',
        organization: '',
      },
      member: {
        subscriberId: '',
        firstName: '',
        lastName: '',
        birthDate: '1980-01-01',
        insurance: '',
        memberId: '',
        recaptcha: '',
        username: '',
      },
      insurances: [],
      dialog: false,
      registerDialog: false,
      userType: '',
      email: '',
      forgotPasswordDialog: false,
      showRegistration: false,
      emailRules: [
        (v) => !!v || 'Email is required',
        (v) => /.+@.+\..+/.test(v) || 'Email must be valid',
      ],
      passwordRule: [(v) => !!v || 'Password is required'],
      error: false,
      passwordRules: [
        (v) => !!v || 'Password required',
        (v) => (v && v.length >= 5) || 'Password must have 5+ characters',
        (v) => /(?=.*[A-Z])/.test(v) || 'Must have one uppercase character',
        (v) => /(?=.*\d)/.test(v) || 'Must have one number',
      ],
      passwordConfirm: [
        (v) => !!v || 'Confirm password',
        (v) => v === this.user.password || 'Passwords do not match',
      ],
      loading: false,
      errorMessage: '',
      menu: false,
      loginInvalid: '',
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      showSSOButton: process.env.VUE_APP_ACTIVATE_SSO === 'true',
    };
  },
  mounted() {
    this.getInsurances();
    this.getUser();
    // Get the user type from the local storage when the component is mounted
    const userType = localStorage.getItem('userType');
    if (userType) {
      this.userType = userType;
    } else {
      // If the user type is not set, show the dialog
      this.dialog = true;
    }
  },
  methods: {
    setLoadingButton(){
      this.loading = false;
      this.registerDialog = false;
    },
    submit(e) {
      if (this.$refs.form.validate()) {
        e.preventDefault();
        this.loading = true;
        this.error = false;
        this.errorMessage = '';
        const email = this.user.email.toLowerCase();
        const { password } = this.user;

        this.login(email, password);
      }
    },
    setUserType() {
      localStorage.setItem('userType', this.userType);
      this.dialog = false;
    },
    async ssoLogin() {
      // CHECK SSO URL
      try {
        window.location.href='/api/saml/elderone/redirect';
        // await axios.get('/api/saml/elderone/redirect');
        // window.location.replace(response.data.SSO_LINK);
      } catch (errors) {
        console.log('SSO link not available');
      }
      // REDIRECT TO SSO URL
      console.log('login');
    },
    async register(e) {
      if (this.$refs.form.validate()) {
        e.preventDefault();
        this.loading = true;
        const email = this.user.email.toLowerCase();
        const username = this.user.name.toLowerCase();
        const { password, npi, authCode, insurance, organization } = this.user;
        console.log(this.user);

        const recaptchaToken = true;

        try {
          const typeResponse = await axios.get(
            `api/list/provider/type/${encodeURIComponent(String(npi).trim())}`
          );
          const userRole = typeResponse.data && typeResponse.data.role;
          if (!userRole) {
            throw new Error('Could not determine account type for this NPI.');
          }

          const data = {
            email,
            username,
            password,
            role: userRole,
            insurance,
            organization,
            firstLogin: false,
            npi,
            authCode,
            recaptchaToken,
          };

          await axios.post('/api/register', data);
          setTimeout(() => {
            this.login(email, password);
          }, 2000);
        } catch (err) {
          this.error = true;
          const data = err.response && err.response.data;
          this.errorMessage =
            (typeof data === 'string' && data) ||
            (data && (data.message || data.error)) ||
            err.message ||
            'Registration failed.';
          console.log('Cannot register', data || err.message);
        } finally {
          this.loading = false;
        }
      }
    },
    async getUser() {
      await axios.get('/api/user').then(() => {
        this.$router.push('/membership');
      });
    },
    findRecords(e) {
      e.preventDefault();
      this.loading = true;

      if (this.insurances.length > 0) {
        this.member.insurance = this.insurances[0];
      }

      const data = this.member;
      const findRecord = async () => {
        await axios
          .post('/api/records', data)
          .then((response) => {
            this.member.memberId = response.data[0].SUBSCRIBER_ID;
            this.member.username = response.data[0].MEMBER_NAME;
            this.registerDialog = true;
          })
          .catch(() => {
            this.errorMessage = true;
            this.loading = false;
          });
      };
      findRecord();
    },
    login(email, password) {
      const data = {
        email,
        password,
      };
      this.loginInvalid = '';
      axios
        .post('/api/login', data)
        .then((response) => {
          // PRIORIDADE 1: Verificar se precisa trocar senha PRIMEIRO (antes de salvar qualquer coisa)
          if (response.data.user && (response.data.user.firstLogin != null ? response.data.user.firstLogin : response.data.user.first_login) === true) {
            // Salvar token de troca de senha no localStorage PRIMEIRO
            if (response.data.user.resetPasswordToken) {
              localStorage.setItem('resetPasswordToken', response.data.user.resetPasswordToken);
              const expiry = Date.now() + 2 * 60 * 60 * 1000;
              localStorage.setItem('resetPasswordTokenExpiry', String(expiry));
            }
            // Salvar dados do usuário no localStorage DEPOIS do token
            localStorage.setItem('user', JSON.stringify(response.data.user));
            // Redirecionar para troca de senha imediatamente
            console.log('🔐 Password change required, redirecting to /pwchange');
            this.$router.push(`/pwchange/${response.data.user.resetPasswordToken}`);
            return; // IMPORTANTE: retornar para não executar o código abaixo
          }
          
          localStorage.removeItem('resetPasswordToken');
          localStorage.removeItem('resetPasswordTokenExpiry');
          
          // Salvar dados do usuário normalmente
          localStorage.setItem('user', JSON.stringify(response.data.user));
          
          // PRIORIDADE 2: Redirecionar baseado no role e insurance
          if (response.data.user.role === 'Clinical') {
            this.$router.push('/membership');
          } else if (response.data.user.role === 'Provider') {
            this.$router.push('/membership');
          } else if (response.data.user.role === 'Member') {
            // Verificar se é FamilyHealthChoice para redirecionar para /member-enrollment
            const isFamilyHealthChoice = response.data.user.insurance && 
              Array.isArray(response.data.user.insurance) &&
              response.data.user.insurance.some(ins => 
                ins === 'FamilyHealthChoice' || ins === 'FloridaHealthChoice'
              );
            if (isFamilyHealthChoice) {
              console.log('🏥 FamilyHealthChoice Member, redirecting to /member-enrollment');
              this.$router.push('/member-enrollment');
            } else {
              this.$router.push('/member');
            }
          } else {
            axios.post('/api/users/lastLogin', response.data.user);
            this.$router.push('/membership');
          }
        })
        .catch((errors) => {
          this.loading = false;
          this.error = true;

          if (!errors.response) {
            this.errorMessage = 'Network error. Please check your connection and try again.';
            return;
          }

          if (errors.response.status === 403) {
            axios.post('/api/users/lastLogin', {
              _id: errors.response.data.userId,
            });
            const status = (errors.response.data && errors.response.data.userStatus) || '';
            this.loginInvalid = status.toLowerCase();
            this.errorMessage = status
              ? `This account is ${status}, please contact support.`
              : 'This account cannot access this platform, please contact support.';
          } else if (errors.response.status === 429) {
            // rate limit error
            this.errorMessage =
              "You've made too many unsuccessful login attempts. Please try again after some time.";
          } else if (errors.response.status === 400) {
            // Backend returns [user, "Incorrect username/password.", {message: "Incorrect email."}]
            const data = errors.response.data;
            this.errorMessage = (Array.isArray(data) && data[2] && data[2].message)
              ? data[2].message
              : (Array.isArray(data) && data[1])
                ? data[1]
                : (data && data.message) || 'Incorrect username/password.';
          } else {
            this.errorMessage = 'An error occurred while trying to log in.';
          }
        });
    },

    getInsurances() {
      axios.get('/api/list/insurances').then((response) => {
        this.insurances = response.data;
        this.user.insurance = this.insurances[0];
        this.user.organization = this.insurances[0];
      });
    },
    sendResetEmail() {
      this.loading = true;
      axios
        .post('/api/forgot-password', { email: this.email })
        .then(() => {
          // show success message
          this.loading = false;
          this.forgotPasswordDialog = false;
          // After sending the password reset email:
          this.snackbarMessage = 'A password reset email has been sent if this user exists';
          this.snackbarColor = 'success';
          this.snackbar = true;
        })
        .catch((error) => {
          this.loading = false;
          this.forgotPasswordDialog = false;
          this.snackbarColor = 'error';
          if (error.response && error.response.status === 400) {
            this.snackbarMessage = 'No account with this email address exists.';
          } else if (error.response && error.response.status === 500) {
            this.snackbarMessage = 'Error sending email. Please try again later.';
          } else {
            this.snackbarMessage = 'An error occurred while sending the reset email.';
          }
          this.snackbar = true;
        });
    },
  },
  computed: {
    isFHCClient() {
      const customer = (process.env.VUE_APP_CUSTOMER || '').toLowerCase();
      return customer.includes('familyhealthchoice') || customer.includes('floridahealthchoice');
    },
    isPasswordValid() {
      return this.user.password === this.user.confirmPassword;
    },
    useWhiteMenu() {
      return process.env.VUE_APP_WHITE_MENU === 'true';
    },
    changeUserText() {
      return this.userType === 'member' ? 'Change to Provider View' : 'Change to Member View';
    },
    portalName() {
      return this.userType === 'member' ? 'Member Portal' : 'Provider Portal';
    },
    isProvider: {
      get() {
        return this.userType === 'provider';
      },
      set(value) {
        this.userType = value ? 'provider' : 'member';
      },
    },
    portalTagline() {
      return this.userType === 'member'
        ? 'Empowering members with innovative technology'
        : 'Empowering healthcare providers with innovative technology';
    },
  },
  watch: {
    isPasswordValid(val) {
      this.valid = val;
    },
  },
};
</script>

<style scoped>
.sidebar-bottom-text {
  position: fixed !important;
  bottom: 10px;
  left: 0;
  width: 100%;
}
.login-img {
  background-image: url(../assets/login.jpg);
  background-size: cover;
}

#form {
  background-color: transparent !important;
}

.welcome-text {
  position: absolute;
  top: 55%;
  left: 450px;
}

/* White menu styling for login - Only applies when VUE_APP_WHITE_MENU=true */
.v-navigation-drawer.white-menu-sidebar {
  background-color: white !important;
  
  /* Global text color override */
  * {
    color: #333333 !important;
  }

  /* Tab styling with higher specificity */
  .v-tabs .v-tab {
    color: #333333 !important;
  }
  
  .v-tabs .v-tab.v-tab--active {
    color: #00ADB5 !important;
  }
  
  .v-tabs .v-tabs-slider {
    background-color: #00ADB5 !important;
  }

  /* Form field labels and text - force override */
  .v-text-field .v-label,
  .v-text-field label,
  .v-text-field .v-input__slot,
  .v-select .v-label,
  .v-select label {
    color: #333333 !important;
  }

  /* Input text color - force override */
  .v-text-field input,
  .v-text-field .v-text-field__slot input,
  .v-select .v-select__selection {
    color: #333333 !important;
  }

  /* Button styling - preserve button colors */
  .v-btn:not(.v-btn--text):not(.v-btn--outlined) {
    color: white !important;
  }
  
  .v-btn.v-btn--text {
    color: #333333 !important;
  }

  /* Keep alert colors intact */
  .v-alert,
  .v-alert * {
    color: inherit !important;
  }

  /* Icons in form fields */
  .v-input__icon .v-icon,
  .v-text-field .v-input__prepend-inner .v-icon,
  .v-input__prepend-outer .v-icon,
  .v-input__append-outer .v-icon {
    color: #666666 !important;
  }

  /* Date picker and menu items */
  .v-menu__content * {
    color: #333333 !important;
  }

  /* Checkbox and radio button labels */
  .v-input--selection-controls .v-label {
    color: #333333 !important;
  }

  /* Card text */
  .v-card-text,
  .v-card-text *,
  .v-card-actions,
  .v-card-actions * {
    color: #333333 !important;
  }
  
  /* But preserve button text colors in cards */
  .v-card-actions .v-btn:not(.v-btn--text) {
    color: white !important;
  }
}
</style>
